import { AsyncPipe, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  Signal,
  WritableSignal,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import {
  ButtonComponent,
  IPaginationEvent,
  PaginationComponent,
  SvgIconComponent,
  TableColumnItem,
  TableComponent,
  DatePickerComponent,
  ModalService,
  ITableColumnSortEvent,
  TableColumnSortDirection,
} from '@ui-lib/components';
import {
  IHistoryFormFilters,
  THistoryFormFiltersKeys,
  ISavedHistoryFormFilters,
  TSelectedArrayLikeFilters,
  TSelectedFilters,
  HistorySorting,
  IHistoryItemModalData,
} from './history.model';
import { getHistoryTableColumnsConfig } from './history-table-columns.config';
import { HistoryService, HistorySortExpressionParameter } from './history.service';
import { LoadingState } from '../../shared/types/loading.type';
import { Subject, takeUntil } from 'rxjs';
import { LocalStorageService } from '../../core/services/storage/local-storage.service';
import { UserSelectorComponent } from '../../shared/components/user-selector/user-selector.component';
import { IDictionaryItem } from '../../shared/types/dictionary-item.interface';
import { prepareRequest } from '../../shared/utils/prepare-request.function';
import { HistoryModalComponent } from './components/history-modal/history-modal.component';
import { SearchAuditResponse } from '../../core/openapi/bff-admin-api/model/search-audit-response';
import { PageToolbarComponent } from '../../shared/components/page-toolbar/page-toolbar.component';
import { PermissionService } from '../../core/services/user/permission.service';
import { UserPermissionEnum } from '../../core/models/user-permissions.enum';
import { toSignal } from '@angular/core/rxjs-interop';

const HISTORY_FILTER = 'historyFilters';

@Component({
  standalone: true,
  selector: 'mon-history',
  templateUrl: './history.component.html',
  styleUrls: ['./history.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [HistoryService],
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    ButtonComponent,
    AsyncPipe,
    TableComponent,
    PaginationComponent,
    DatePipe,
    SvgIconComponent,
    FormsModule,
    UserSelectorComponent,
    DatePickerComponent,
    PageToolbarComponent,
  ],
})
export class HistoryComponent implements OnInit, OnDestroy {
  private readonly historyService: HistoryService = inject(HistoryService);
  private readonly localStorageService: LocalStorageService = inject(LocalStorageService);
  private readonly modalService = inject(ModalService);
  private readonly permissionService = inject(PermissionService);

  private readonly subscriptionDestroy$: Subject<void> = new Subject<void>();

  public readonly columns: TableColumnItem[] = getHistoryTableColumnsConfig();

  public readonly history$ = this.historyService.history$;
  public readonly historyTotalCount: Signal<number> = this.historyService.historyTotalCount;
  public readonly historyLoadingStatus: Signal<LoadingState> =
    this.historyService.historyLoadingStatus;

  public readonly currentPage: WritableSignal<number> = signal(1);

  public readonly LoadingState = LoadingState;
  protected readonly canReadUsers = toSignal(
    this.permissionService.hasPermission(UserPermissionEnum.UserRead),
    { initialValue: false },
  );

  public readonly historyFormFilters = new FormGroup<IHistoryFormFilters>({
    userIds: new FormControl<string[]>([], { nonNullable: true }),
    datePeriod: new FormControl<Date[] | null>(null),
  });

  private selectedUsers: IDictionaryItem<string>[] = [];

  public ngOnInit(): void {
    const savedFilters =
      this.localStorageService.getParsed<ISavedHistoryFormFilters>(HISTORY_FILTER);

    if (savedFilters) {
      this.historyFormFilters.patchValue(
        {
          userIds: this.canReadUsers() ? (savedFilters.userIds ?? []) : [],
          datePeriod: savedFilters.datePeriod
            ? savedFilters.datePeriod.map((date) => new Date(date))
            : null,
        },
        { emitEvent: false },
      );
    }

    this.applyFilters();

    this.historyFormFilters.valueChanges
      .pipe(takeUntil(this.subscriptionDestroy$))
      .subscribe(() => {
        this.currentPage.set(1);

        this.applyFilters();
      });
  }

  public get isFiltersActive(): boolean {
    const { userIds, datePeriod } = this.historyFormFilters.getRawValue();
    return (this.canReadUsers() && userIds.length > 0) || datePeriod !== null;
  }

  public get selectedFiltersCount(): number {
    const { datePeriod } = this.historyFormFilters.getRawValue();
    return (
      this.allSelectedFilters.reduce((count, [, filters]) => count + filters.length, 0) +
      (datePeriod !== null ? 1 : 0)
    );
  }

  public get allSelectedFilters(): TSelectedFilters[] {
    const filters: TSelectedFilters[] = [];

    if (this.canReadUsers() && this.selectedUsers.length > 0) {
      filters.push(['userIds', [...this.selectedUsers]] as TSelectedFilters);
    }

    return filters;
  }

  public clearAllFilters(): void {
    if (!this.isFiltersActive) return;

    this.historyFormFilters.patchValue({
      userIds: [],
      datePeriod: null,
    });
  }

  public onPageChange(paginator: IPaginationEvent): void {
    this.currentPage.set(paginator.pageIndex);
    this.historyService.updateHistory({
      skip: (paginator.pageIndex - 1) * paginator.pageSize,
      take: paginator.pageSize,
    });
  }

  public getHistoryFiltersControl<T>(control: THistoryFormFiltersKeys): FormControl<T> {
    return this.historyFormFilters.get(control) as FormControl<T>;
  }

  public onUsersSelectionChange(items: IDictionaryItem<string>[]): void {
    this.selectedUsers = items;
  }

  public deselectFilter(
    control: TSelectedArrayLikeFilters,
    removeFilter: IDictionaryItem<string>,
  ): void {
    const currentControl = this.historyFormFilters.get(control) as FormControl<string[]>;

    const items = currentControl.value;
    currentControl.setValue(items.filter((item) => item !== removeFilter.id));
  }

  public sortHandler(sortEvent: ITableColumnSortEvent): void {
    let sortableColumn: TableColumnItem | undefined;

    this.columns.forEach((col: TableColumnItem): void => {
      if (col.sort && col.sort.enabled) {
        if (col.propName === sortEvent.column) {
          col.sort.direction = sortEvent.direction;
          sortableColumn = col;
        } else {
          col.sort.direction = TableColumnSortDirection.none;
        }
      }
    });

    const expression: HistorySorting | undefined =
      sortableColumn &&
      sortableColumn.sort &&
      sortableColumn.sort.direction !== TableColumnSortDirection.none
        ? {
            direction: sortableColumn.sort.direction,
            propertyName: sortableColumn.propName,
          }
        : undefined;

    if (expression === undefined) {
      // Restore default
      const defaultSortedColumnName = HistorySortExpressionParameter.propertyName;
      const defaultDirection = HistorySortExpressionParameter.direction;
      this.columns.forEach((col: TableColumnItem): void => {
        if (col.propName === defaultSortedColumnName && col.sort) {
          col.sort.direction = defaultDirection;
        }
      });
    }

    this.currentPage.set(1);
    this.historyService.updateHistory({
      skip: 0,
      sortExpressions: [expression ?? { ...HistorySortExpressionParameter }],
    });
  }

  public onDetailsClick(historyItem: SearchAuditResponse): void {
    this.modalService.open<IHistoryItemModalData>({
      component: HistoryModalComponent,
      data: {
        ...historyItem,
        entitySnapshot: historyItem.entitySnapshot ? JSON.parse(historyItem.entitySnapshot) : null,
      } as IHistoryItemModalData,
      customContainerClass: 'history-modal',
    });
  }

  public ngOnDestroy(): void {
    this.subscriptionDestroy$.next();
    this.subscriptionDestroy$.complete();
  }

  private applyFilters(): void {
    const filters = this.historyFormFilters.getRawValue();

    const preparedFilters = prepareRequest(filters);
    const allowedFilters = {
      ...preparedFilters,
      userIds: this.canReadUsers() ? preparedFilters.userIds : undefined,
    };

    this.localStorageService.set(HISTORY_FILTER, allowedFilters);

    this.historyService.updateHistory({
      ...allowedFilters,
      datePeriod: allowedFilters.datePeriod
        ? {
            startDateTime: allowedFilters.datePeriod[0]?.toISOString(),
            endDateTime: allowedFilters.datePeriod[1]?.toISOString(),
          }
        : undefined,
    });
  }
}
